/**
 * Position-change validation — "Karriera & Kompensimi".
 *
 * Frontend mirror of backend/services/positionChange.js. Both sides must agree:
 * the UI hides/blocks the current position, and the API rejects it again if a
 * request is crafted by hand. Keep the rule AND the message identical.
 *
 * Comparison is by position ID: workers.position is free TEXT that drifts in
 * casing/whitespace, and positions.name is UNIQUE so distinct ids always mean
 * distinct positions. Names are a fallback only, for legacy workers whose
 * position_id was never backfilled.
 */

export const POSITION_SAME_MESSAGE = 'Pozita e re duhet të jetë e ndryshme nga pozita aktuale.';

/** Numeric position id, or null when the value is absent / not an id. */
export function toPositionId(v) {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  if (s === '') return null;
  if (!/^\d+$/.test(s)) return null;          // a name like "HR Officer" is not an id
  const n = Number(s);
  return Number.isSafeInteger(n) && n > 0 ? n : null;
}

/** Casing- and whitespace-insensitive name key, or '' when there is no name. */
export function normalizePositionName(v) {
  if (v === null || v === undefined) return '';
  return String(v).trim().replace(/\s+/g, ' ').toLocaleLowerCase('sq');
}

/**
 * Is the proposed position the one the worker already holds?
 * Returns true only when it can be PROVEN to be the same.
 */
export function isSamePosition(current, proposed) {
  const curId = toPositionId(current?.id);
  const newId = toPositionId(proposed?.id);

  if (curId !== null && newId !== null) return curId === newId;

  const curName = normalizePositionName(current?.name);
  const newName = normalizePositionName(proposed?.name);
  if (curName === '' || newName === '') return false;
  return curName === newName;
}

/**
 * The worker's position as {id, name}, from whatever the worker payload has.
 * selectworker returns PositionId (mapped in workerFromDb) + Position (name).
 */
export function currentPositionOf(worker) {
  if (!worker) return { id: null, name: '' };
  return {
    id:   toPositionId(worker.PositionId ?? worker.position_id),
    name: String(worker.Position ?? worker.position ?? '').trim(),
  };
}

/**
 * Drop the worker's current position from the picker (requirement: it may be
 * excluded or disabled — excluding keeps the list shorter and unambiguous).
 */
export function selectablePositions(options, current) {
  return (Array.isArray(options) ? options : []).filter(
    (o) => o && !isSamePosition(current, { id: o.id, name: o.name })
  );
}
